import { createLocation } from 'history';
import { toUserLocation } from 'ts-components/PiChat/location';
import {
  isNavigatePath,
  type NavigateFailureCode,
  type NavigateResult,
  type ServerMessage,
  type UserLocation,
} from 'ts-components/PiChat/protocol';
import {
  RESULT_DEADLINE_MARGIN_MS,
  resolveResultUpload,
  type ResolvedResultUpload,
} from 'ts-components/PiChat/presignedPut';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import type {
  GuardTarget,
  NavigationGuardRegistry,
} from 'ts-components/NavigationGuard/navigationGuards';

export const NAVIGATE_MESSAGES = {
  invalidPath: 'The path is not a valid app-relative URL',
  declined: 'The user chose to keep their unsaved changes',
  notMoved: 'The page blocked the navigation',
  noTimeToConfirm:
    'The page has unsaved changes and the request is about to expire',
  busy: 'Another navigation is still waiting for the user',
  ended: 'The chat closed before the user answered',
  pathUnreported: 'The new page address could not be reported',
} as const;

type NavigateFailure = Extract<NavigateResult, { ok: false }>;

const navigateFailure = (
  code: NavigateFailureCode,
  message: string
): NavigateFailure => ({ ok: false, code, message });

export type NavigateFrame = Extract<ServerMessage, { type: 'ui.navigate' }>;

const LABEL = 'ui.navigate';
const TOO_LARGE = navigateFailure(
  'unavailable',
  NAVIGATE_MESSAGES.pathUnreported
);

export type NavigateControllerState =
  | { status: 'idle' }
  | { status: 'confirming'; requestId: string; path: string };

export interface NavigateControllerDeps {
  /** Encoded pathname + search + hash. */
  getPath(): string;
  registry: Pick<NavigationGuardRegistry, 'wouldBlock' | 'runBypassed'>;
  push(path: string): void;
  sendResult(
    upload: ResolvedResultUpload<NavigateResult>,
    result: NavigateResult
  ): unknown;
}

export interface NavigateController {
  handle(frame: NavigateFrame, meta: PiChatFrameMeta): void;
  confirm(): void;
  cancel(): void;
  dispose(): void;
  getState(): NavigateControllerState;
  subscribe(listener: () => void): () => void;
}

export const locationPath = ({
  pathname,
  search,
  hash,
}: Pick<Location, 'pathname' | 'search' | 'hash'>) =>
  `${pathname}${search}${hash}`;

/** The location history hands a Prompt for `path`; null when the path or its decoded pathname is invalid. */
const guardTarget = (path: string): GuardTarget | null => {
  if (!isNavigatePath(path)) return null;
  try {
    const { pathname, search, hash } = createLocation(path);
    decodeURI(pathname);
    return isNavigatePath(pathname) ? { pathname, search, hash } : null;
  } catch {
    return null;
  }
};

const IDLE: NavigateControllerState = { status: 'idle' };

interface Pending {
  upload: ResolvedResultUpload<NavigateResult>;
  path: string;
  /** The connection the request arrived on; confirming re-checks it is still current. */
  meta: PiChatFrameMeta;
  timer: ReturnType<typeof setTimeout> | undefined;
  onAbort: () => void;
}

export const createNavigateController = (
  deps: NavigateControllerDeps
): NavigateController => {
  const listeners = new Set<() => void>();
  let state: NavigateControllerState = IDLE;
  let pending: Pending | null = null;

  const setState = (next: NavigateControllerState) => {
    state = next;
    listeners.forEach((listener) => listener());
  };

  const takePending = (): Pending | null => {
    const taken = pending;
    if (!taken) return null;
    pending = null;
    clearTimeout(taken.timer);
    taken.meta.signal.removeEventListener('abort', taken.onAbort);
    setState(IDLE);
    return taken;
  };

  const navigate = (path: string, bypass: boolean): NavigateResult => {
    const before = deps.getPath();
    if (before === path) return { ok: true, path };
    if (bypass) {
      deps.registry.runBypassed(() => deps.push(path));
    } else {
      deps.push(path);
    }
    const after = deps.getPath();
    if (after === before) {
      return navigateFailure('unavailable', NAVIGATE_MESSAGES.notMoved);
    }
    return isNavigatePath(after)
      ? { ok: true, path: after }
      : navigateFailure('unavailable', NAVIGATE_MESSAGES.pathUnreported);
  };

  const finish = (result: () => NavigateResult) => {
    const taken = takePending();
    if (taken) deps.sendResult(taken.upload, result());
  };

  const ended = () => navigateFailure('unavailable', NAVIGATE_MESSAGES.ended);

  const run = (
    frame: NavigateFrame,
    meta: PiChatFrameMeta,
    upload: ResolvedResultUpload<NavigateResult>
  ) => {
    const { path } = frame;
    const target = guardTarget(path);
    if (!target) {
      deps.sendResult(
        upload,
        navigateFailure('unavailable', NAVIGATE_MESSAGES.invalidPath)
      );
      return;
    }
    if (deps.getPath() === path || !deps.registry.wouldBlock(target, 'PUSH')) {
      deps.sendResult(upload, navigate(path, false));
      return;
    }

    const waitMs = upload.expiresAt - RESULT_DEADLINE_MARGIN_MS - Date.now();
    if (waitMs <= 0) {
      deps.sendResult(
        upload,
        navigateFailure('unavailable', NAVIGATE_MESSAGES.noTimeToConfirm)
      );
      return;
    }
    const request: Pending = {
      upload,
      path,
      meta,
      timer: undefined,
      onAbort: () => {
        // The connection that asked is gone: settle so the dialog closes and a new request isn't `busy`.
        if (pending === request) finish(ended);
      },
    };
    meta.signal.addEventListener('abort', request.onAbort);
    request.timer = setTimeout(
      () =>
        finish(() =>
          navigateFailure('unavailable', NAVIGATE_MESSAGES.noTimeToConfirm)
        ),
      waitMs
    );
    pending = request;
    setState({ status: 'confirming', requestId: frame.requestId, path });
  };

  return {
    handle(frame, meta) {
      if (meta.signal.aborted || !meta.isCurrent()) return;
      const upload = resolveResultUpload(
        frame.upload,
        meta.url,
        LABEL,
        TOO_LARGE
      );
      if (!upload || Date.now() >= upload.expiresAt) return;
      if (pending) {
        deps.sendResult(
          upload,
          navigateFailure('busy', NAVIGATE_MESSAGES.busy)
        );
        return;
      }
      run(frame, meta, upload);
    },

    confirm() {
      if (!pending) return;
      const { path, meta } = pending;
      // Never bypass guards for a request whose connection has since closed or been replaced.
      const current = !meta.signal.aborted && meta.isCurrent();
      finish(current ? () => navigate(path, true) : ended);
    },

    cancel() {
      finish(() => navigateFailure('declined', NAVIGATE_MESSAGES.declined));
    },

    dispose() {
      finish(ended);
    },

    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export interface KiCodeNavigationWiring {
  controller: NavigateController;
  handleServerMessage(message: ServerMessage, meta: PiChatFrameMeta): void;
  getLocation(): UserLocation | undefined;
  dispose(): void;
}

export const createKiCodeNavigation = (
  deps: NavigateControllerDeps
): KiCodeNavigationWiring => {
  const controller = createNavigateController(deps);
  return {
    controller,
    handleServerMessage: (message, meta) => {
      if (message.type === 'ui.navigate') controller.handle(message, meta);
    },
    getLocation: () => toUserLocation(deps.getPath()),
    dispose: () => controller.dispose(),
  };
};
